import type { FilterSelection } from '@/scopes/prices/domain/filters/types'
import type { FiltersAPIRawResponse } from '@/scopes/prices/infrastructure/filters/types'
import {
  createQueryStringSerializer,
  type QueryStringSerializer,
} from '@/scopes/common/infrastructure/query-strings/queryStringSerializer'
import { createPricesFiltersParser } from '@/scopes/prices/infrastructure/pricesFiltersParser'
import defaultFetch from 'isomorphic-unfetch'
import { createFiltersResponseMapper, type FiltersResponseMapper, MappedFiltersResponse } from './filtersResponseMapper'

interface FiltersContext {
  accoId: string
  contextItemId: string
  bookingGateId: string
}

export type FiltersCriteria = FiltersContext & { filterSelection: FilterSelection }

interface Dependencies {
  env: {
    NEXT_PUBLIC_API_BASE_URL: string
  }
  fetch: typeof defaultFetch
  queryStringSerializer: QueryStringSerializer<FiltersContext & FilterSelection>
  filtersResponseMapper: FiltersResponseMapper
}

export interface FiltersApiClient {
  getFilters: (criteria: FiltersCriteria) => Promise<MappedFiltersResponse>
}

export function createFiltersApiClient(dependencies: Partial<Dependencies> = {}): FiltersApiClient {
  const {
    env = {
      NEXT_PUBLIC_API_BASE_URL: process.env.NEXT_PUBLIC_API_BASE_URL,
    },
    fetch = defaultFetch,
    queryStringSerializer = createQueryStringSerializer({
      parser: createPricesFiltersParser(),
    }),
    filtersResponseMapper = createFiltersResponseMapper(),
  } = dependencies as Dependencies

  const fetchOptions: RequestInit = {
    referrerPolicy: 'no-referrer-when-downgrade',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
    },
  }

  async function getFilters(criteria: FiltersCriteria) {
    const url = buildAPIUrl(criteria)

    const response = await fetch(url, fetchOptions)
    if (response.status !== 200)
      return Promise.reject(new Error('error loading filters'))
    const data = (await response.json()) as FiltersAPIRawResponse

    return filtersResponseMapper.map(data, criteria.filterSelection.Participants)
  }

  function buildAPIUrl({ accoId, contextItemId, bookingGateId, filterSelection }: FiltersCriteria) {
    const filtersContext = {
      accoId,
      bookingGateId,
      contextItemId,
    }

    const queryString = queryStringSerializer.serialize({
      ...filtersContext,
      ...filterSelection,
    })

    return `${env.NEXT_PUBLIC_API_BASE_URL}/SharedFilters/GetFiltersApi?${queryString}`
  }

  return { getFilters }
}
